import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  Link,
  Menu,
  MenuItem,
} from "@mui/material";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import CenteredCardLayout from "layouts/CenteredTableView/CenteredCardLayout";
import { isAdmin } from "layouts/Config";
import SearchIcon from "assets/icons1/search_icon.svg";
import {
  validatePageQuery,
  validatePerPageQuery,
  initialPageFilterState,
  convertESTtoUserLocalDateAndTime,
  handleErrorMessage,
  debounceEventHandler,
  openSuccessNotification,
} from "helpers/methods";
import { pageFilters } from "models/interfaces";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { contactsTableHeader, StatusColors } from "../ContactsConstants";
import CustomTable from "global/components/CustomTable/CustomTable";
import { cloneDeep } from "lodash";
import { RouterLink } from "components/router-link";
import viewpaths from "global/constants/ViewPathConstants";
import {
  deleteArchiveContact,
  fetchArchivedContactsCount,
  fetchArchivedContacts,
} from "../ContactsService";
import contactsStyles from "../Contacts.styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { KeyboardBackspace } from "@mui/icons-material";
import history from "utils/history";
import strings from "global/constants/StringConstants";
import ConfirmationModal from "screens/Templates/Components/ConfirmationModal";
import CustomLoader from "global/components/CustomLoader/CustomLoader";

const ArchivedContacts = () => {
  const theme = useTheme();
  const classes = contactsStyles(theme);
  const searchTerm = useRef<any>(null);
  const [contacts, setContacts] = useState<any>();
  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const urlParams = new URLSearchParams(useLocation().search);
  const pageFromUrl = validatePageQuery(urlParams.get("page"));
  const rowsPerPageFromUrl = validatePerPageQuery(urlParams.get("rowsPerPage"));
  const contactTypeFromUrl = urlParams.get("category");
  const searchFromUrl = urlParams.get("search");
  const contactStatusFromUrl = urlParams.get("status");
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openSingleDeleteModal, setOpenSingleDeleteModal] =
    useState<boolean>(false);
  const [contactsFilters, setContactsFilters] = useState<pageFilters>(
    initialPageFilterState(
      pageFromUrl,
      rowsPerPageFromUrl,
      searchFromUrl ?? "",
      contactTypeFromUrl ?? "all",
      contactStatusFromUrl ?? "4"
    )
  );
  const [selectedContactIds, setSelectedContactIds] = useState<
    { id: number; status: number }[]
  >([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [contactId, setContactId] = useState<{ id: number; status: number }>({
    id: 0,
    status: 0,
  });

  useEffect(() => {
    const { page, rowsPerPage, searchText, dropdownValue, status } =
      contactsFilters;
    if (searchTerm.current !== null) searchTerm.current.value = searchText;
    window.history.replaceState(
      null,
      "",
      `?page=${page}&rowsPerPage=${rowsPerPage}&category=${dropdownValue}&status=${status}`
    );
    getTableData();
  }, [contactsFilters]);

  const getTableData = async () => {
    const { searchText, dropdownValue, page, rowsPerPage, status } =
      contactsFilters;
    try {
      setIsLoading(true);
      const count = await fetchArchivedContactsCount(
        dropdownValue!,
        searchText,
        +status!
      );
      setCount(count);
      await getTableDataAccordingToPage(
        count,
        dropdownValue!,
        page,
        searchText.trim(),
        rowsPerPage,
        +status!
      );
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTableDataAccordingToPage = async (
    count: number,
    dropdownValue: string,
    page: number,
    searchText: string,
    rowsPerPage: number,
    status: number
  ) => {
    try {
      let contactsRes: any[] = [];
      setIsLoading(true);
      contactsRes = await fetchArchivedContacts(
        page,
        rowsPerPage,
        dropdownValue,
        searchText,
        status
      );
      const tableData = convertDataToTableFormat(contactsRes);
      setContacts([...tableData]);
    } catch (error: any) {
      setIsLoading(false);
      throw error;
    }
  };

  const resetStates = () => {
    setOpenDeleteModal(false);
    setOpenSingleDeleteModal(false);
    setSelectedContactIds([]);
    setContactId({
      id: 0,
      status: 0,
    });
    setContactsFilters({
      ...contactsFilters,
      page: 1,
    });
  };

  const searchChangeHandler = (event: any) => {
    const searchText = event.target.value;
    setContactsFilters({
      ...contactsFilters,
      page: 1,
      searchText,
    });
  };

  const pageChangeHandler = (event: any, newPage: number) => {
    setContactsFilters({
      ...contactsFilters,
      page: newPage,
    });
  };

  const perPageRowsChangeHandler = (rowPerPage: number) => {
    setContactsFilters({
      ...contactsFilters,
      rowsPerPage: rowPerPage,
      page: 1,
    });
  };

  const onSelectOne = (id: number, status: number) => {
    const selectedIds = cloneDeep(selectedContactIds);
    selectedIds.push({ id, status });
    setSelectedContactIds(selectedIds);
  };

  const onDeselectOne = (id: number) => {
    setSelectedContactIds(
      selectedContactIds.filter((select) => select.id !== id)
    );
  };

  const handleDelete = async (
    contactData: { id: number; status: number }[]
  ) => {
    try {
      setLoading(true);
      const ids = contactData.map((contact) => contact.id);
      const res = await deleteArchiveContact(ids);
      setOpenDeleteModal(false);
      setSelectedContactIds([]);
      openSuccessNotification(res.message);
      resetStates();
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleOpenMenu = (event: any, id: number, status: number) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setContactId({ id, status });
  };

  const handleMenuItemClick = (action: string) => {
    switch (action) {
      case "delete":
        setAnchorEl(null);
        setOpenSingleDeleteModal(true);
        break;
      default:
        break;
    }
  };

  const convertDataToTableFormat = (data: any) => {
    return data.map((contact: any) => {
      const fullName = [contact.firstName, contact.lastName]
        .filter(Boolean)
        .join(" ");

      return {
        rowId: contact.id,
        email: (
          <Stack direction="column">
            <Link
              color="text.primary"
              variant="subtitle2"
              href={`${viewpaths.viewContactViewPath}/${contact.id}?fromArchived=true`}
              component={RouterLink}
            >
              {contact.emailId}
            </Link>
          </Stack>
        ),
        name: (
          <Tooltip
            title={fullName ? fullName : "N/A"}
            arrow
            placement="top-start"
          >
            <Typography sx={{ color: "#475569" }} variant="subtitle2">
              {fullName
                ? fullName.length > 20
                  ? `${fullName.slice(0, 20)}...`
                  : fullName
                : "N/A"}
            </Typography>
          </Tooltip>
        ),
        status: (
          <Typography
            sx={{
              color: StatusColors[contact.status],
              display: "flex",
              alignItems: "center",
            }}
            variant="subtitle2"
          >
            <Box
              sx={{
                ...classes.contactStatusBox,
                backgroundColor: StatusColors[contact.status],
              }}
            />
            Archived
          </Typography>
        ),
        statusRow: contact.status,
        createdOn: (
          <Typography sx={{ color: "#475569" }} variant="subtitle2">
            {convertESTtoUserLocalDateAndTime(
              contact.createdOn,
              "MMMM D, YYYY [at] h:mm A"
            )}
          </Typography>
        ),
        action: (
          <Stack direction="row" spacing={4} mt={1} alignItems="center">
            <IconButton
              size="small"
              sx={{
                color: theme.palette.primary.dark,
                padding: 0,
              }}
              onClick={(e: any) => {
                e.stopPropagation();
                handleOpenMenu(e, contact.id, contact.status);
              }}
            >
              <MoreVertIcon />
            </IconButton>
          </Stack>
        ),
      };
    });
  };

  const getFilters = () => {
    return (
      <CenteredCardLayout removeLastChildBottomPadding={true}>
        <Stack>
          <TextField
            id="contacts_search_box"
            placeholder="Search Contacts"
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&.MuiOutlinedInput-root.Mui-focused": {
                border: "none !important",
              },
            }}
            autoComplete="off"
            fullWidth
            onChange={debounceEventHandler(
              searchChangeHandler,
              strings.SEARCH_TIME_OUT
            )}
            inputRef={searchTerm}
            InputProps={{
              startAdornment: (
                <Stack mr={1}>
                  <img src={SearchIcon} width="30px" height="20px" />
                </Stack>
              ),
            }}
          />
        </Stack>
      </CenteredCardLayout>
    );
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const moreOptions = () => {
    return (
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem
          key="delete"
          value="delete"
          sx={classes.dropDownMenuItem}
          onClick={() => handleMenuItemClick("delete")}
        >
          Delete
        </MenuItem>
      </Menu>
    );
  };
  const getArchivedCOntacts = () => {
    return (
      <Stack direction="column" spacing={2}>
        <Stack direction="row" alignItems="center">
          <Link
            color="text.primary"
            href={viewpaths.contactsViewPath}
            sx={{
              alignItems: "center",
              display: "inline-flex",
            }}
            onClick={(e) => {
              e.preventDefault();
              history.push(viewpaths.contactsViewPath);
            }}
            underline="hover"
            component={RouterLink}
          >
            <KeyboardBackspace
              htmlColor="#475569"
              sx={{ cursor: "pointer", pr: 1 }}
            />
            <Typography variant="body1" color="text.primary" fontWeight={700}>
              Contacts
            </Typography>
          </Link>
        </Stack>
        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Typography variant="h5" color="text.primary">
            Archived Contacts
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              id="archived_delete_btn"
              variant="contained"
              sx={{
                background: "#d72727",
                "&:hover": {
                  background: "#d72727",
                },
              }}
              disabled={isLoading || count === 0}
              onClick={() => setOpenDeleteModal(true)}
            >
              {selectedContactIds.length > 0
                ? `Delete ${selectedContactIds.length} contact(s) permanently`
                : "Delete all permanently"}
            </Button>
          </Stack>
        </Stack>
        {getFilters()}
        <CenteredCardLayout removeLastChildBottomPadding={true}>
          <Box>
            <CustomTable
              headers={contactsTableHeader()}
              rows={contacts}
              page={contactsFilters.page}
              rowsPerPage={contactsFilters.rowsPerPage}
              onRowsPerPageChange={perPageRowsChangeHandler}
              onPageChange={pageChangeHandler}
              count={count}
              showJumpToPageNumber={false}
              handleOnChangePageInput={pageChangeHandler}
              showPagination={true}
              isLoading={isLoading}
              showCheckbox={isAdmin()}
              onSelectOne={(id: number) => {
                const contactData = contacts.find(
                  (contact: any) => contact.rowId === id
                );
                if (contactData) {
                  onSelectOne(id, contactData?.statusRow);
                }
              }}
              onDeselectOne={onDeselectOne}
              onSelectAll={() => {
                const pageContactsData = contacts.map((row: any) => ({
                  id: row.rowId,
                  status: row.statusRow,
                }));
                setSelectedContactIds((prev) => {
                  const existingIds = prev.map((item) => item.id);
                  const newContacts = pageContactsData.filter(
                    (contact: any) => !existingIds.includes(contact.id)
                  );
                  return [...prev, ...newContacts];
                });
              }}
              onDeselectAll={() => {
                const pageIds = contacts.map((row: any) => row.rowId);
                setSelectedContactIds((prev) =>
                  prev.filter((item) => !pageIds.includes(item.id))
                );
              }}
              selected={selectedContactIds.map((item) => item.id)}
            />
          </Box>
        </CenteredCardLayout>
      </Stack>
    );
  };
  return (
    <>
      {isAdmin() && (
        <CenteredAuthenticatedLayout>
          {getArchivedCOntacts()}
        </CenteredAuthenticatedLayout>
      )}
      {moreOptions()}
      {openDeleteModal && (
        <ConfirmationModal
          title="Delete contact"
          subtitle2="Deleting contact(s) is permanent and cannot be undone. Are you sure you want to delete this contact?"
          isVisible={openDeleteModal}
          btn1OnClick={() => setOpenDeleteModal(false)}
          btn1Title="Cancel"
          btn1Variant="outlined"
          btn2OnClick={() => handleDelete(selectedContactIds)}
          btn2Title="Delete"
          btn2Variant="contained"
          handleDialogClose={() => setOpenDeleteModal(false)}
        />
      )}
      {openSingleDeleteModal && (
        <ConfirmationModal
          title="Delete contact"
          subtitle2="Deleting contact is permanent and cannot be undone. Are you sure you want to delete this contact?"
          isVisible={openSingleDeleteModal}
          btn1OnClick={() => setOpenSingleDeleteModal(false)}
          btn1Title="Cancel"
          btn1Variant="outlined"
          btn2OnClick={() => handleDelete([contactId])}
          btn2Title="Delete"
          btn2Variant="contained"
          handleDialogClose={() => setOpenSingleDeleteModal(false)}
        />
      )}
      <CustomLoader isLoading={loading} />
    </>
  );
};

export default ArchivedContacts;
